import { LoanChecklist, LoanChecklistRule } from 'src/services/loans/types';

export const getChecklistDisplayOrder = (
  checklist: LoanChecklist,
  fallback: number,
) =>
  typeof checklist.displayOrder === 'number'
    ? checklist.displayOrder
    : fallback;

export const sortChecklistsByDisplayOrder = <T extends LoanChecklist>(
  checklists: T[],
) =>
  checklists
    .map((checklist, index) => ({ checklist, index }))
    .sort(
      (a, b) =>
        getChecklistDisplayOrder(a.checklist, Number.MAX_SAFE_INTEGER) -
          getChecklistDisplayOrder(b.checklist, Number.MAX_SAFE_INTEGER) ||
        a.index - b.index,
    )
    .map(({ checklist }) => checklist);

export const normalizeChecklistsByDisplayOrder = <T extends LoanChecklist>(
  checklists: T[],
) =>
  sortChecklistsByDisplayOrder(checklists).map((checklist, index) => ({
    ...checklist,
    displayOrder: index + 1,
  }));

export const resequenceChecklists = <T extends LoanChecklist>(
  checklists: T[],
) =>
  checklists.map((checklist, index) => ({
    ...checklist,
    displayOrder: index + 1,
  }));

export const getRuleSequence = (rule: LoanChecklistRule, fallback: number) =>
  typeof rule.definition?.ruleSequenceNo === 'number'
    ? rule.definition.ruleSequenceNo
    : fallback;

export const sortRulesBySequence = <T extends LoanChecklistRule>(
  rules: T[],
): T[] =>
  rules
    .map((rule, index) => ({ rule, index }))
    .sort(
      (a, b) =>
        getRuleSequence(a.rule, Number.MAX_SAFE_INTEGER) -
          getRuleSequence(b.rule, Number.MAX_SAFE_INTEGER) || a.index - b.index,
    )
    .map(({ rule }) => rule);

export const resequenceRules = <T extends LoanChecklistRule>(rules: T[]): T[] =>
  rules.map((rule, index) => ({
    ...rule,
    definition: {
      ...rule.definition,
      ruleSequenceNo: index + 1,
    },
  }));

export const normalizeRulesBySequence = <T extends LoanChecklistRule>(
  rules: T[],
) => resequenceRules(sortRulesBySequence(rules));

type ChecklistOrderedRule = {
  checklistDisplayOrder: number;
  ruleSequenceNo: number;
  indexId: number;
};

export const sortProcessedRulesByChecklistAndRuleOrder = <
  T extends ChecklistOrderedRule,
>(
  rules: T[],
): T[] =>
  [...rules].sort(
    (a, b) =>
      a.checklistDisplayOrder - b.checklistDisplayOrder ||
      a.ruleSequenceNo - b.ruleSequenceNo ||
      a.indexId - b.indexId,
  );
